import React from 'react';
import PropTypes from 'prop-types';
import BackToTopIcon from 'mdi-react/ArrowUpIcon';
import { InView } from 'react-intersection-observer';
import loadable from '@loadable/component';

import { AppContext } from '@prisjakt/ui-app-context';
import { connect } from 'react-redux';
import graphqlPropTypes from '@prisjakt/graphql-proptypes';
import Breadcrumb from '@prisjakt/ui-breadcrumb';
import { ScrollLink, ScrollElement } from '@prisjakt/ui-scroll';
import Panel from '@prisjakt/ui-panel';
import T from '@prisjakt/ui-translate';

import RelatedProducts from './components/related-products';
import PriceList from './components/price-list';
import PriceHistory from './components/price-history';
import ProductPriceHistory from '../product-price-history';
import PreReleaseInformation from './components/pre-release-information';
import StickyTrack from '../../components/sticky-track';
import UserReviews from './components/user-reviews';
import ProductProperties from './components/product-properties';
import Lazy from '../../components/lazy';
import PageContent from '../../components/page-content';
import getCampaignColor from '../../globals/helpers/campaign/get-campaign-color';
import { ViewportContext } from '../../components/viewport';
import AdContainer from '../../components/ads';
import CampaignDeals from '../../components/campaign-deals';
import Modal from '../../components/modal';
import ModalActions from '../../components/modal/actions';
import RecentlyVisitedProducts from '../../components/recently-visited';
import ErrorBoundary from '../../components/error-boundary';
import PromoBanner from '../../components/campaign-banners/promo-banner';
import FeedbackButton from '../feedback/feedback-button';
import ProductHeader from './components/product-header';
import TabList from './components/tab-list';
import ReadableProductInformation from './components/readable-product-information';
import { PRODUCT_ALERT_MODAL_ID } from './components/product-header/constants';
import { getHeaderHeight, getScrollLinkOffset } from './utils';
import productPageQuery from './query.graphql';
import { ProductPageFragment } from '../../types';

import * as S from './styles';

const CreateAlert = loadable(() =>
  import(
    /* webpackChunkName: "extra" */ '../product-alert/components/create-alert'
  )
);

const returnNull = () => null;

const PricelistWithExperiment = ({ product, currentCampaign }) => {
  return (
    <PriceList
      {...product}
      adOffset={1}
      campaign={currentCampaign}
      includeSecondary
      productName={product.name}
    />
  );
};

class ProductPage extends React.PureComponent {
  constructor(...args) {
    super(...args);
    this.state = { isHeaderVisible: true };
    this.onHeaderVisibilityChange = this.onHeaderVisibilityChange.bind(this);
  }

  onHeaderVisibilityChange(isVisible) {
    if (this.state.isHeaderVisible !== isVisible) {
      this.setState({ isHeaderVisible: isVisible });
    }
  }

  render() {
    const {
      activeCampaign,
      review,
      showModal,
      hideModal,
      campaignBanners,
      currentModal,
      routeSection,
      fetchMoreData,
      me,
    } = this.props;

    const { product, importantDates } = this.props.data;
    const { t, linkTo, market } = this.context;
    const privacyPolicyUri =
      market &&
      market.privacySettings &&
      market.privacySettings.responsive &&
      market.privacySettings.responsive.privacyUrl;

    const currentCampaign =
      activeCampaign &&
      product.campaigns &&
      product.campaigns
        .filter(c => c.id === activeCampaign.id)
        .map(match => ({
          ...match,
          name: activeCampaign.name,
          path: `${activeCampaign.web_uri.responsive}${
            product.category ? `?category=${product.category.id}` : ''
          }`,

          media: {
            colors: {
              background: getCampaignColor(match.id, 'background'),
              text: getCampaignColor(match.id, 'text'),
            },
          },
        }))
        .find(() => true);

    const campaignPromoBanners = {
      before:
        campaignBanners &&
        campaignBanners.active.filter(
          b => b && b.type === 'BEFORE_PROMO_BANNER'
        ),
      during:
        campaignBanners &&
        campaignBanners.active.filter(
          b => b && b.type === 'DURING_PROMO_BANNER'
        ),
    };

    // TODO: Mutate product data in data container instead of contextual rendering
    const userReviews =
      review.userReviews && review.reviewType === 'product'
        ? review.userReviews
        : product.userReviews;

    const relatedProducts = product.related && product.related.products;

    const isPreRelease =
      product.prerelease &&
      product.releaseDate &&
      Date.parse(product.releaseDate) > new Date();

    const hasRelatedProducts =
      !isPreRelease && relatedProducts && relatedProducts.length > 0;

    const { isHeaderVisible } = this.state;

    return (
      <>
        {me && me.analyticsAccess && (
          <FeedbackButton position="right">
            <T domain="feedback">button.text</T>
          </FeedbackButton>
        )}

        <PageContent withAds>
          <S.ProductPage>
            {currentModal === PRODUCT_ALERT_MODAL_ID && (
              <Modal
                id={PRODUCT_ALERT_MODAL_ID}
                onRequestClose={hideModal}
                width={500}
              >
                <h2>
                  <T domain="product">alert.title</T>
                </h2>
                <S.Modal noMargin>
                  <p>
                    <T domain="product">alert.lead-text</T>
                  </p>
                  <CreateAlert
                    product={product}
                    showCancel={false}
                    onCreate={hideModal}
                    onRemove={hideModal}
                    privacyPolicyUri={privacyPolicyUri || undefined}
                  />
                </S.Modal>
              </Modal>
            )}
            {campaignPromoBanners.during &&
              campaignPromoBanners.during.length > 0 && (
                <div className="column small-12">
                  <PromoBanner banners={campaignPromoBanners.during} />
                </div>
              )}

            <S.BreadCrumbWrapper>
              <Breadcrumb
                homeText={t('common', 'breadcrumb.home')}
                path={product.category ? product.category.path : undefined}
                firstPathSelectable
              />
            </S.BreadCrumbWrapper>
            <ScrollElement name="Top">
              <ViewportContext.Consumer>
                {({ isMobile }) => (
                  <>
                    <InView
                      onChange={this.onHeaderVisibilityChange}
                      rootMargin={`-${getHeaderHeight(isMobile) - 10}px`}
                    >
                      {({ ref }) => (
                        <ProductHeader
                          ref={ref}
                          title={product.name}
                          product={product}
                          currentCampaign={currentCampaign}
                          showModal={showModal}
                        />
                      )}
                    </InView>

                    <ScrollLink
                      to="Top"
                      onClick={ProductPage.blurActiveElement}
                    >
                      <S.BackToTopButton
                        aria-label={t(
                          'product',
                          'back-to-top.button-description'
                        )}
                      >
                        <BackToTopIcon />
                      </S.BackToTopButton>
                    </ScrollLink>

                    <ScrollElement name="Content">
                      <StickyTrack
                        topMargin={0}
                        headerHeight={getHeaderHeight(isMobile)}
                        renderElement={
                          <TabList
                            routeSection={routeSection}
                            product={product}
                            isSticky={!isHeaderVisible}
                          />
                        }
                      >
                        <S.TabContentWrapper
                          spacingTop={routeSection !== 'main'}
                        >
                          <S.TabContent id="main">
                            {routeSection === 'main' && (
                              <>
                                {isPreRelease && product.releaseDate && (
                                  <PreReleaseInformation
                                    product={product}
                                    releaseDate={product.releaseDate}
                                  />
                                )}
                                {product.statistics &&
                                  product.statistics.pageInfo.lowestPrice &&
                                  isPreRelease && (
                                    <PriceHistory
                                      route={linkTo(
                                        'productPriceHistory',
                                        product
                                      )}
                                      productName={product.name}
                                      {...product.statistics}
                                      predictedPrices={
                                        product.priceForecast
                                          ? product.priceForecast
                                          : undefined
                                      }
                                      preRelease
                                    />
                                  )}
                                {isPreRelease &&
                                  relatedProducts &&
                                  relatedProducts.length > 0 && (
                                    <RelatedProducts
                                      resources={relatedProducts}
                                      category={product.category}
                                      product={product}
                                    />
                                  )}
                                {product.prices && (
                                  <ScrollElement
                                    name="Pricelist"
                                    data-test="MainTabContent"
                                  >
                                    <PricelistWithExperiment
                                      product={product}
                                      currentCampaign={currentCampaign}
                                    />
                                  </ScrollElement>
                                )}
                                <ReadableProductInformation product={product} />
                              </>
                            )}
                          </S.TabContent>

                          <S.TabContent id="properties">
                            {routeSection === 'properties' && (
                              <ErrorBoundary onError={returnNull}>
                                <ScrollElement
                                  name="Product Properties"
                                  data-test="PropertiesTabContent"
                                >
                                  <ProductProperties
                                    basicInfo={this.basicInfo()}
                                    properties={product.properties}
                                    description={product.description}
                                    getScrollLinkOffset={getScrollLinkOffset}
                                  />
                                </ScrollElement>
                              </ErrorBoundary>
                            )}
                          </S.TabContent>

                          <S.TabContent id="reviews">
                            {routeSection === 'reviews' && (
                              <ErrorBoundary onError={returnNull}>
                                <ScrollElement
                                  name="Reviews"
                                  data-test="ReviewsTabContent"
                                >
                                  <UserReviews
                                    belongsTo={{
                                      id: product.id,
                                      name: product.name,
                                      type: 'product',
                                    }}
                                    myReview={
                                      userReviews && userReviews.myReview
                                    }
                                    summary={product.userReviewSummary}
                                    nodes={userReviews && userReviews.nodes}
                                    pageInfo={
                                      userReviews && userReviews.pageInfo
                                    }
                                  />
                                </ScrollElement>
                              </ErrorBoundary>
                            )}
                          </S.TabContent>

                          <S.TabContent id="statistics">
                            {routeSection === 'statistics' && (
                              <ErrorBoundary onError={returnNull}>
                                <ScrollElement
                                  name="Product Information"
                                  data-test="StatisticsTabContent"
                                >
                                  <ProductPriceHistory
                                    data={{ product, importantDates }}
                                    fetchMoreData={fetchMoreData}
                                  />
                                </ScrollElement>
                              </ErrorBoundary>
                            )}
                          </S.TabContent>

                          <Panel>
                            <AdContainer
                              placement="panorama_target"
                              min={861}
                              max={10000}
                              scalable
                              scaleBase=".pj-ui-panel--row"
                              isReady
                            />
                          </Panel>

                          {product.category &&
                            product.category.deals &&
                            product.category.deals.length > 0 && (
                              <ErrorBoundary onError={returnNull}>
                                <Lazy offset={100}>
                                  <CampaignDeals
                                    deals={product.category.deals}
                                    campaignLinkQuery={`?category=${product.category.id}`}
                                  />
                                </Lazy>
                              </ErrorBoundary>
                            )}

                          <Lazy offset={100}>
                            <RecentlyVisitedProducts offset={1} />
                          </Lazy>

                          {hasRelatedProducts && (
                            <ScrollElement name="Related products">
                              <RelatedProducts
                                resources={relatedProducts}
                                category={product.category}
                                product={product}
                              />
                            </ScrollElement>
                          )}

                          {campaignPromoBanners.before &&
                            campaignPromoBanners.before.length > 0 && (
                              <PromoBanner
                                inverted
                                banners={campaignPromoBanners.before}
                              />
                            )}
                        </S.TabContentWrapper>
                      </StickyTrack>
                    </ScrollElement>
                  </>
                )}
              </ViewportContext.Consumer>
            </ScrollElement>
          </S.ProductPage>
        </PageContent>
      </>
    );
  }

  static blurActiveElement() {
    if (document.activeElement) {
      document.activeElement.blur();
    }
  }

  // Transform some basic information about the product into a DataList-friendly shape to pass into ProductProperties
  basicInfo() {
    const { product } = this.props.data;
    const { t } = this.context;

    if (!product) {
      return [];
    }

    const { brand, category } = product;
    const brandProperties = brand
      ? [
          {
            name: t('product', 'property.brand'),
            value: {
              type: 'string',
              text: brand.name,
              link: brand.pathName,
            },
          },
        ]
      : [];
    const categoryProperties = category
      ? [
          {
            name: t('product', 'property.category'),
            value: {
              type: 'string',
              text: category.name,
              link: category.pathName,
            },
          },
        ]
      : [];

    return [
      {
        name: t('product', 'property.product-name'),
        value: { type: 'string', text: product.name },
      },
    ].concat(brandProperties, categoryProperties);
  }
}

ProductPage.graphQL = productPageQuery;

// Group on product ID, ignore section param
ProductPage.group = ({ name, match }) =>
  `${name}_${match && match.params ? match.params.id : 'unknown'}`;

ProductPage.variables = ({ id, offset, section }) => ({
  id: parseInt(id, 10),
  offset: parseInt(offset, 10) || 0,
  section,
  statisticsTime: section === 'statistics' ? '1970-01-02' : undefined,
});

ProductPage.loadOrder = (
  { section = 'main' } = {},
  loaded = {},
  options = { cacheRefresh: false, initialRoute: false }
) => {
  const sectionContent = {
    main: {
      priceList: true,
    },
    properties: {
      productProperties: true,
    },
    reviews: {
      userReviews: true,
    },
    statistics: {
      priceHistory: true,
      importantDates: true,
      priceHistoryTable: true,
      priceTrend: true,
      medianPrice: true,
    },
  };

  const cacheMoreData = options.cacheRefresh
    ? {
        badges: true,
        media: true,
        relatedProducts: true,
        priceHistory: true,
      }
    : {};

  return [
    {
      main: !loaded.main,
      ...cacheMoreData,
      ...sectionContent[section],
    },
    {
      // Refresh price list for cached pages only
      priceList: options.initialRoute && section === 'main',
      userActions: !loaded.userActions,
      badges: !loaded.badges,
      media: !loaded.media,
      campaign: !loaded.campaign,
      relatedProducts: !loaded.relatedProducts,
      campaignDeals: !loaded.campaignDeals,
      priceHistory: !loaded.priceHistory,
    },
  ];
};

ProductPage.contextType = AppContext;

const { number, string, shape, arrayOf, object, func, oneOf, bool } = PropTypes;
ProductPage.propTypes = {
  data: shape({
    product: graphqlPropTypes(ProductPageFragment),
  }),
  review: object, // eslint-disable-line react/forbid-prop-types
  activeCampaign: shape({
    id: number,
    name: string,
    type: string,
    state: string,
    slug: string,
    web_uri: shape({
      mobile: string,
      desktop: string,
      responsive: string,
    }),
    banners: arrayOf(
      shape({
        type: string,
        url: string,
        imageUrl: string,
      })
    ),
  }),
  showModal: func.isRequired,
  hideModal: func.isRequired,
  campaignBanners: shape({
    active: arrayOf(object),
  }),
  routeSection: oneOf(['main', 'properties', 'reviews', 'statistics']),
  fetchMoreData: func,
  currentModal: string,
  me: shape({
    analyticsAccess: bool,
  }),
};

ProductPage.defaultProps = {
  data: {
    product: undefined,
    page: undefined,
  },
  activeCampaign: undefined,
  review: {},
  campaignBanners: undefined,
  routeSection: 'main',
  fetchMoreData: () => {},
  currentModal: '',
  me: {
    analyticsAccess: false,
  },
};

export default connect(
  state => ({
    campaignBanners: state.globals.campaignBanners,
    review: state.areas.review,
    currentModal: state.modal.current,
    me: state.areas.account.me,
  }),
  ModalActions
)(ProductPage);
